import type { PublicProfile } from "@pax/api-contract/common";
import { type QueryClient, queryOptions, useQuery } from "@tanstack/react-query";
import { fetchClientConfig, orpc, publicOrpc, UNREACHABLE_MESSAGE } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { observePaxRequest } from "@/lib/network";
import { checkInSession, checksSessions } from "@/lib/risk";
import { broadcastAccountChange, broadcastSignOut, subscribeToAuthChanges } from "./auth-changes";

type SessionResult = Awaited<ReturnType<typeof authClient.getSession>>;
/** The signed-in user and their session, or null when nobody is signed in. */
export type Session = NonNullable<SessionResult["data"]>;

export const sessionKey = ["session"] as const;
const accountDataKeys = [[["games"]], [["accounting"]], [["profiles"]], [["accounts"]]] as const;

/**
 * Who is signed in, as one cached answer the shell and the auth guard share.
 *
 * Better Auth's own `authClient.useSession` keeps a second store; routing
 * everything through the query cache means a sign-in or sign-out updates one
 * place and every reader agrees at once.
 */
export const sessionQuery = queryOptions({
  queryKey: sessionKey,
  queryFn: async (): Promise<Session | null> => {
    if (!navigator.onLine) throw new Error(UNREACHABLE_MESSAGE);

    const { data, error } = await observePaxRequest(() => authClient.getSession());
    if (error) {
      const message = error.status === 404 || error.status >= 500 ? "" : (error.message ?? "");
      throw new Error(message.trim() || UNREACHABLE_MESSAGE);
    }

    if (data) broadcastAccountChange(data.user.id);
    return data;
  },
  staleTime: 5 * 60_000,
});

/** How long after an account changes the shell keeps checking for its imported sign-in photo. */
const SIGN_IN_PHOTO_WAIT_MS = 30_000;
const SIGN_IN_PHOTO_CHECK_MS = 3_000;
/** Bounds the checks even when the browser's clock disagrees with the server's `updatedAt`. */
const SIGN_IN_PHOTO_CHECKS = SIGN_IN_PHOTO_WAIT_MS / SIGN_IN_PHOTO_CHECK_MS;

/** The API imports a new account's sign-in photo a few seconds after sign-in, so it isn't there yet. */
function awaitingSignInPhoto(
  user: Session["user"],
  profile: PublicProfile | undefined,
  reads: number,
): boolean {
  return (
    Boolean(user.image) &&
    profile?.avatar === null &&
    reads <= SIGN_IN_PHOTO_CHECKS &&
    Date.now() - new Date(user.updatedAt).getTime() < SIGN_IN_PHOTO_WAIT_MS
  );
}

/**
 * The signed-in account's public profile, which is what the shell paints. It
 * is read once the session names the profile id. Guests have no public
 * profile, so their query never runs. Right after sign-in it's read again
 * every few seconds until the imported sign-in photo arrives.
 */
export function useCurrentProfile(user: Session["user"] | undefined) {
  return useQuery({
    ...orpc.profiles.get.queryOptions({
      input: { params: { profileId: user?.id ?? "" } },
      select: response => response.profile,
    }),
    enabled: user !== undefined && !user.isAnonymous,
    refetchInterval: query =>
      user && awaitingSignInPhoto(user, query.state.data?.profile, query.state.dataUpdateCount)
        ? SIGN_IN_PHOTO_CHECK_MS
        : false,
  });
}

/**
 * Who is signed in, for components. `data` is the session, or `null` when
 * signed out. Loaders and event handlers use `sessionQuery` with the query
 * client instead, since they run outside React.
 */
export function useSession() {
  return useQuery(sessionQuery);
}

export const clientConfigQuery = queryOptions({
  queryKey: ["client-config"],
  queryFn: fetchClientConfig,
  staleTime: Number.POSITIVE_INFINITY,
});

function setSession(queryClient: QueryClient, session: Session | null): void {
  queryClient.setQueryData(sessionKey, session);
}

/** Also refreshes preset reads, whose viewer state belongs to whoever was signed in. */
export function clearAccountQueryData(queryClient: QueryClient): void {
  for (const queryKey of accountDataKeys) queryClient.removeQueries({ queryKey });
  void queryClient.invalidateQueries({ queryKey: publicOrpc.presets.get.key() });
}

export async function signOut(queryClient: QueryClient): Promise<void> {
  try {
    try {
      const session = queryClient.getQueryData<Session | null>(sessionKey);
      if (session?.session.impersonatedBy) {
        await authClient.admin.stopImpersonating();
      }
    } finally {
      await authClient.signOut();
    }
  } finally {
    setSession(queryClient, null);
    clearAccountQueryData(queryClient);
    broadcastSignOut();
  }
}

/**
 * Checks in each session this tab sees, once it's checked: a free-credit claim, which grants
 * signup credits and the daily refresh. A pending session is checked first, on the Checking page.
 */
export function checkInSessions(queryClient: QueryClient): () => void {
  let checkedInSessionId: string | undefined;
  return queryClient.getQueryCache().subscribe(event => {
    if (event.query.queryKey[0] !== sessionKey[0]) return;
    const session = queryClient.getQueryData<Session | null>(sessionKey);
    if (!session || session.session.id === checkedInSessionId) return;
    const sessionId = session.session.id;
    checkedInSessionId = sessionId;
    void (async () => {
      // A pending session checks in once the Checking page has checked it and the session reloads.
      if (!session.session.checkedAt && (await checksSessions())) {
        if (checkedInSessionId === sessionId) checkedInSessionId = undefined;
        return;
      }
      if (await checkInSession({ accountId: session.user.id })) {
        void queryClient.invalidateQueries({ queryKey: [["accounting"]] });
      }
    })().catch(() => undefined);
  });
}

export function synchronizeAuthAcrossTabs(
  queryClient: QueryClient,
  refreshActiveRoutes: () => void | Promise<void>,
): () => void {
  const stopAuthChanges = subscribeToAuthChanges(change => {
    if (change.type === "signed-out") {
      void queryClient.cancelQueries({ queryKey: sessionKey, exact: true });
      setSession(queryClient, null);
      clearAccountQueryData(queryClient);
      void refreshActiveRoutes();
      return;
    }

    // A tab that has not read its session yet will read the new account anyway.
    const session = queryClient.getQueryData<Session | null>(sessionKey);
    if (session === undefined || session?.user.id === change.userId) return;

    clearAccountQueryData(queryClient);
    void queryClient
      .resetQueries({ queryKey: sessionKey, exact: true })
      .then(() => refreshActiveRoutes());
  });

  const verifyAfterReconnect = () =>
    void queryClient.invalidateQueries({ queryKey: sessionKey, exact: true });
  window.addEventListener("online", verifyAfterReconnect);

  return () => {
    stopAuthChanges();
    window.removeEventListener("online", verifyAfterReconnect);
  };
}
